import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { Shield, Lock, FileText, Truck, RotateCcw, AlertCircle, Mail, Phone } from 'lucide-react';

/* ── Shared helpers ─────────────────────────────────────────── */
const Bullet = ({ children }) => (
  <li className="flex items-start">
    <span className="w-2 h-2 bg-[#548B2E] rounded-full mt-2 mr-3 flex-shrink-0" />
    <span>{children}</span>
  </li>
);
Bullet.propTypes = { children: PropTypes.node.isRequired };

const SectionHeading = ({ children }) => (
  <h3 className="text-xl font-bold text-gray-800 mt-10 mb-4 pb-2 border-b border-gray-100">{children}</h3>
);
SectionHeading.propTypes = { children: PropTypes.node.isRequired };

const SubHeading = ({ children }) => (
  <h4 className="text-base font-semibold text-gray-700 mt-5 mb-2">{children}</h4>
);
SubHeading.propTypes = { children: PropTypes.node.isRequired };

const ContactBox = ({ children }) => (
  <div className="mt-10 p-5 bg-[#f2f8eb] rounded-xl border border-[#c8e6a0] text-sm text-gray-700 space-y-1">
    {children}
  </div>
);
ContactBox.propTypes = { children: PropTypes.node.isRequired };

/* ── Tab definitions ─────────────────────────────────────────── */
const TABS = [
  { id: 'privacy',   label: 'Privacy Policy',        icon: <Lock className="w-4 h-4" /> },
  { id: 'terms',     label: 'Terms & Conditions',    icon: <FileText className="w-4 h-4" /> },
  { id: 'shipping',  label: 'Shipping & Delivery',   icon: <Truck className="w-4 h-4" /> },
  { id: 'refund',    label: 'Refund & Cancellation', icon: <RotateCcw className="w-4 h-4" /> },
];

/* ══════════════════════════════════════════════════════════════
   PRIVACY POLICY
══════════════════════════════════════════════════════════════ */
const PrivacyContent = () => (
  <div className="prose prose-lg max-w-none text-gray-600">
    <p className="text-base leading-relaxed mb-2">
      Book My Fresh Private Limited ("Book My Fresh", "we", "our", or "us") respects your privacy and is committed to
      protecting your personal data in accordance with the <strong>Digital Personal Data Protection Act, 2023
      ("DPDPA")</strong> and the Digital Personal Data Protection Rules, 2025. This Privacy Policy explains what
      personal data we collect, why we collect it, how we use it, and your rights as a Data Principal under applicable
      law.
    </p>

    <SectionHeading>1. Information We Collect</SectionHeading>
    <p className="mb-3">We collect the following personal data only for the specific purpose stated against it:</p>
    <ul className="space-y-3 list-none pl-0">
      <Bullet><strong>Full Name</strong> — to create and manage your account, and to address order communications.</Bullet>
      <Bullet><strong>Mobile Number</strong> — to verify your identity via OTP, send order confirmations and transactional updates, and enable delivery coordination.</Bullet>
      <Bullet><strong>Email Address</strong> (optional) — to send account-related notifications and, where separately consented to, promotional communications.</Bullet>
      <Bullet><strong>Delivery Address</strong> — to fulfil your order and deliver products to your specified location.</Bullet>
      <Bullet><strong>GPS Location Data</strong> (foreground only, during active order placement) — to verify your delivery address, confirm service availability, and improve delivery accuracy. GPS location data is deleted upon successful delivery completion and is not retained thereafter.</Bullet>
      <Bullet><strong>Order History</strong> — to provide customer support, process refunds and replacements, and resolve disputes.</Bullet>
      <Bullet><strong>Payment Transaction Records</strong> (order amount, transaction ID, payment status) — for order confirmation and dispute resolution. Payment card, UPI, and bank account details are processed exclusively by Razorpay and are not stored on Book My Fresh servers.</Bullet>
    </ul>

    <SectionHeading>2. How We Use Your Information</SectionHeading>
    <ul className="space-y-3 list-none pl-0">
      <Bullet>Process and deliver orders</Bullet>
      <Bullet>Provide customer support</Bullet>
      <Bullet>Verify user accounts</Bullet>
      <Bullet>Send order updates and transactional communications</Bullet>
      <Bullet>Send promotional offers and notifications (subject to your separate consent)</Bullet>
      <Bullet>Improve our services and Platform functionality</Bullet>
      <Bullet>Ensure security and fraud prevention</Bullet>
    </ul>

    <SectionHeading>3. Marketing Communications</SectionHeading>
    <p className="mb-3">
      Book My Fresh may send you marketing communications including SMS, WhatsApp messages, push notifications, and
      promotional emails, subject to your separate and explicit consent obtained at registration or through your
      in-app preferences. Marketing communications are optional and are not a condition of using the Platform.
    </p>
    <p className="mb-3">
      To withdraw consent, go to <strong>Settings &gt; Privacy &gt; Communication Preferences</strong> in the app
      or contact us at <a href="mailto:grievances@bookmyfresh.com" className="text-[#548B2E] font-medium">grievances@bookmyfresh.com</a>.
      Withdrawal takes effect immediately.
    </p>
    <p>
      Transactional communications (order confirmations, OTPs, delivery updates, payment receipts) will always be
      sent as they are necessary for service delivery.
    </p>

    <SectionHeading>4. Data Sharing</SectionHeading>
    <p className="mb-3">We share personal data only with the following named third parties for the specific purposes stated:</p>
    <ul className="space-y-3 list-none pl-0">
      <Bullet><strong>Razorpay (Payment Gateway)</strong> — your name, mobile number, email address (if provided), order amount, and order ID are shared solely to process your payment.</Bullet>
      <Bullet><strong>Amazon Web Services (AWS Lightsail)</strong> — our cloud infrastructure provider hosting our application and database in the ap-south-1 (Mumbai, India) region. Your personal data remains within India at all times.</Bullet>
      <Bullet><strong>Firebase Authentication (Google)</strong> — your mobile number and device token are shared for OTP verification and push notification delivery only.</Bullet>
      <Bullet><strong>Google Maps Platform</strong> — your GPS coordinates and location search queries are shared to display delivery address maps and confirm service availability.</Bullet>
      <Bullet><strong>Government and regulatory authorities</strong> — where disclosure is required by applicable law, court order, or lawful direction of a government authority.</Bullet>
    </ul>
    <p className="mt-4">
      Delivery is managed directly by Book My Fresh through our own delivery team. Our delivery personnel access your
      name, mobile number, and delivery address solely to fulfil your order.
    </p>

    <SectionHeading>5. Data Security</SectionHeading>
    <ul className="space-y-3 list-none pl-0">
      <Bullet><strong>Encryption in transit</strong> — all data transmitted between your device and our servers is encrypted using HTTPS/TLS.</Bullet>
      <Bullet><strong>Role-based access controls</strong> — access to personal data is restricted to authorised personnel based on their operational role.</Bullet>
      <Bullet><strong>India data residency</strong> — all personal data is stored on AWS Lightsail infrastructure located in Mumbai, India.</Bullet>
    </ul>
    <p className="mt-4">
      In the event of a personal data breach, we will notify you and the Data Protection Board of India in accordance
      with Rule 7 of the DPDPA Rules, 2025.
    </p>

    <SectionHeading>6. Data Retention</SectionHeading>
    <ul className="space-y-3 list-none pl-0">
      <Bullet><strong>Account data (active accounts)</strong> — retained for the duration of your account; deleted immediately upon account deletion.</Bullet>
      <Bullet><strong>Order history</strong> — retained as required for dispute resolution, customer support, and applicable legal obligations.</Bullet>
      <Bullet><strong>Payment transaction records</strong> — retained as required under applicable financial and tax regulations including the GST Act.</Bullet>
      <Bullet><strong>Processing logs</strong> — retained for a minimum of 1 year from the date of processing as required under Rule 8(3) of the DPDPA Rules, 2025.</Bullet>
      <Bullet><strong>GPS location data</strong> — deleted upon successful delivery completion. Not retained thereafter.</Bullet>
    </ul>
    <p className="mt-4">
      Account deletion requests will be actioned within <strong>3 working days</strong>. Contact us at
      {' '}<a href="mailto:grievances@bookmyfresh.com" className="text-[#548B2E] font-medium">grievances@bookmyfresh.com</a>.
    </p>

    <SectionHeading>7. Your Rights (Data Principal Rights)</SectionHeading>
    <ul className="space-y-3 list-none pl-0">
      <Bullet><strong>Right of Access (Section 11, DPDPA)</strong> — you may request a summary of your personal data and the purposes for which it is being processed.</Bullet>
      <Bullet><strong>Right of Correction and Erasure (Section 12, DPDPA)</strong> — you may request correction of inaccurate data or erasure where it is no longer necessary, subject to legal retention obligations.</Bullet>
      <Bullet><strong>Right of Grievance Redressal (Section 13, DPDPA)</strong> — you may raise a grievance with our Grievance Officer. We will respond within 30 days and in any event within 90 days.</Bullet>
      <Bullet><strong>Right of Nomination (Section 14, DPDPA)</strong> — you may nominate any individual to exercise your data protection rights on your behalf in the event of your death or incapacity.</Bullet>
    </ul>

    <SectionHeading>8. Children's Privacy</SectionHeading>
    <p>
      The Platform is intended for users who are <strong>18 years of age or older</strong>. Book My Fresh does not
      knowingly collect personal data of persons below 18 years of age. If you believe that a person below 18 years
      has registered, please notify us at{' '}
      <a href="mailto:grievances@bookmyfresh.com" className="text-[#548B2E] font-medium">grievances@bookmyfresh.com</a>.
    </p>

    <SectionHeading>9. Changes to This Policy</SectionHeading>
    <p>
      For material changes, we will notify registered users via SMS or WhatsApp before the changes take effect. For
      changes requiring fresh consent under the DPDPA, we will obtain your consent before implementing such changes.
      The current version is available at{' '}
      <span className="text-[#548B2E] font-medium">https://www.bookmyfresh.com/privacy-policy</span>.
    </p>

    <ContactBox>
      <p className="font-semibold text-gray-800 mb-2">Designated Privacy Contact & Grievance Officer</p>
      <p><strong>Name:</strong> Sheetal Ojha</p>
      <p><strong>Designation:</strong> Grievance Officer</p>
      <p><strong>Organization:</strong> Book My Fresh Private Limited</p>
      <p className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-[#548B2E]" /> <a href="mailto:grievances@bookmyfresh.com" className="text-[#548B2E] font-medium">grievances@bookmyfresh.com</a></p>
      <p className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-[#548B2E]" /> +91 8078622096</p>
      <p className="mt-2 text-gray-500">General support: <a href="mailto:info@bookmyfresh.com" className="text-[#548B2E]">info@bookmyfresh.com</a></p>
      <p className="text-gray-500">Registered Office: Jaipur, Rajasthan</p>
    </ContactBox>
  </div>
);

/* ══════════════════════════════════════════════════════════════
   TERMS & CONDITIONS
══════════════════════════════════════════════════════════════ */
const TermsContent = () => (
  <div className="prose prose-lg max-w-none text-gray-600">
    <p className="text-base leading-relaxed mb-2">
      By accessing or using the Book My Fresh mobile application, website, or related services, you agree to comply
      with and be bound by these Terms and Conditions.
    </p>
    <p className="text-sm text-gray-500 mt-2 mb-6">
      <strong>Data Protection:</strong> The collection, use, storage, and processing of your personal data is governed
      exclusively by our Privacy Policy. These Terms govern the commercial relationship between you and Book My Fresh.
    </p>

    <SectionHeading>1. Eligibility</SectionHeading>
    <p>
      Users must be at least <strong>18 years of age</strong> to register or place orders. Contracts entered into
      by persons below 18 years are void under the Indian Contract Act, 1872. If Book My Fresh becomes aware that a
      registered user is below 18 years of age, the account and associated personal data will be deleted without delay.
    </p>

    <SectionHeading>2. Services</SectionHeading>
    <ul className="space-y-3 list-none pl-0">
      <Bullet>Book My Fresh Private Limited provides online ordering and delivery of fruits, vegetables, and other grocery products within serviceable locations.</Bullet>
      <Bullet>Services are currently available in Jaipur, Rajasthan. The Company may expand to additional cities in the future.</Bullet>
    </ul>

    <SectionHeading>3. Product Information</SectionHeading>
    <ul className="space-y-3 list-none pl-0">
      <Bullet>We strive to provide accurate product descriptions and images. Actual products may vary slightly due to seasonal, natural, or supplier-related variations.</Bullet>
      <Bullet><strong>Hydro Organic Disclaimer:</strong> Not all products available on Book My Fresh are Hydro Organic. Only products specifically marked or labelled as "Hydro Organic" should be considered as such.</Bullet>
    </ul>

    <SectionHeading>4. Pricing</SectionHeading>
    <ul className="space-y-3 list-none pl-0">
      <Bullet>All prices are displayed in Indian Rupees (INR).</Bullet>
      <Bullet>The price applicable to your order is the price confirmed at the time of order placement.</Bullet>
      <Bullet>Book My Fresh reserves the right to update prices on the Platform, which will apply to future orders only.</Bullet>
    </ul>

    <SectionHeading>5. Delivery Policy</SectionHeading>
    <p className="mb-3 text-sm text-gray-500">Full details are available in our <button className="text-[#548B2E] font-medium underline-offset-2 underline">Shipping & Delivery Policy</button> tab.</p>
    <SubHeading>Instant / On-Demand Delivery</SubHeading>
    <ul className="space-y-3 list-none pl-0">
      <Bullet>Available in selected serviceable pin codes between 6:00 AM and 11:00 PM.</Bullet>
      <Bullet>Target delivery time: 30–40 minutes from order confirmation.</Bullet>
      <Bullet>Free delivery or delivery charges apply based on order threshold, as displayed at checkout.</Bullet>
    </ul>
    <SubHeading>Scheduled / Next-Day Delivery</SubHeading>
    <ul className="space-y-3 list-none pl-0">
      <Bullet>Delivery window: 5:00 AM to 9:00 AM on the selected delivery date.</Bullet>
      <Bullet>Applicable minimum order values and delivery charges will be displayed at checkout.</Bullet>
    </ul>
    <p className="mt-3 text-sm">
      Applicable delivery charges, if any, will be displayed at checkout before order confirmation. Delivery timelines
      are indicative and may vary due to operational, weather, traffic, or other unforeseen factors.
    </p>

    <SectionHeading>6. Order Cancellation</SectionHeading>
    <p className="mb-3 text-sm text-gray-500">Full details are available in our <button className="text-[#548B2E] font-medium underline-offset-2 underline">Refund & Cancellation Policy</button> tab.</p>
    <ul className="space-y-3 list-none pl-0">
      <Bullet>Customers may request cancellation of an order, including after dispatch, subject to operational feasibility.</Bullet>
      <Bullet>Book My Fresh reserves the right to cancel any order due to product unavailability, payment issues, pricing errors, or operational reasons.</Bullet>
      <Bullet>Where Book My Fresh cancels a confirmed order, the full payment received will be refunded to the original payment source within 4–5 business days of cancellation.</Bullet>
    </ul>

    <SectionHeading>7. Refunds and Replacements</SectionHeading>
    <p className="mb-3 text-sm text-gray-500">Full details are available in our <button className="text-[#548B2E] font-medium underline-offset-2 underline">Refund & Cancellation Policy</button> tab.</p>
    <ul className="space-y-3 list-none pl-0">
      <Bullet>Customers must report quality issues within 24 hours of delivery.</Bullet>
      <Bullet>Depending on the nature of the issue, Book My Fresh may offer a replacement, store credit, or refund.</Bullet>
      <Bullet>Approved refunds will be processed to the original payment source within 4–5 business days of approval.</Bullet>
      <Bullet>At the customer's request, refunds may alternatively be credited to Book My Fresh Store Credits for faster processing.</Bullet>
      <Bullet>Book My Fresh Store Credits are a convenience feature and are not a Prepaid Payment Instrument regulated by the Reserve Bank of India.</Bullet>
    </ul>

    <SectionHeading>8. Accepted Payment Methods</SectionHeading>
    <ul className="space-y-3 list-none pl-0">
      <Bullet>UPI</Bullet>
      <Bullet>Debit Cards</Bullet>
      <Bullet>Credit Cards</Bullet>
      <Bullet>Net Banking</Bullet>
      <Bullet>Book My Fresh Store Credits (BMF Coins)</Bullet>
      <Bullet>Cash on Delivery (COD)</Bullet>
    </ul>

    <SectionHeading>9. User Conduct</SectionHeading>
    <p className="mb-3">Users agree not to:</p>
    <ul className="space-y-3 list-none pl-0">
      <Bullet>Provide false information</Bullet>
      <Bullet>Misuse the Platform or engage in fraudulent activities</Bullet>
      <Bullet>Interfere with Platform operations</Bullet>
    </ul>

    <SectionHeading>10. Limitation of Liability</SectionHeading>
    <ul className="space-y-3 list-none pl-0">
      <Bullet>To the maximum extent permitted by applicable law, Book My Fresh's aggregate liability for any claim arising out of or in connection with an order shall not exceed the value of that order.</Bullet>
      <Bullet>Nothing in these Terms limits liability for: (a) death or personal injury caused by our negligence; (b) fraud or fraudulent misrepresentation; or (c) any liability that cannot be excluded under applicable law, including under the Consumer Protection Act, 2019.</Bullet>
    </ul>

    <SectionHeading>11. Changes to Terms</SectionHeading>
    <p>
      For changes to commercial terms, continued use of the Platform following publication constitutes acceptance. For
      changes affecting personal data processing, we will notify you through SMS or WhatsApp and obtain your consent
      where required. The current version is available at{' '}
      <span className="text-[#548B2E] font-medium">https://www.bookmyfresh.com/terms-and-conditions</span>.
    </p>

    <ContactBox>
      <p className="font-semibold text-gray-800 mb-2">Grievance Officer</p>
      <p><strong>Name:</strong> Sheetal Ojha &nbsp;|&nbsp; <strong>Designation:</strong> Grievance Officer &amp; Creative Director</p>
      <p className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-[#548B2E]" /> <a href="mailto:grievances@bookmyfresh.com" className="text-[#548B2E] font-medium">grievances@bookmyfresh.com</a></p>
      <p className="mt-1 text-gray-500">The Grievance Officer will acknowledge your complaint within 48 hours and endeavour to resolve it within 30 days.</p>
      <p className="text-gray-500">General support: <a href="mailto:info@bookmyfresh.com" className="text-[#548B2E]">info@bookmyfresh.com</a> &nbsp;|&nbsp; <Phone className="w-3 h-3 inline" /> +91 8078622096</p>
      <p className="text-gray-500">Registered Office: Jaipur, Rajasthan</p>
    </ContactBox>
  </div>
);

/* ══════════════════════════════════════════════════════════════
   SHIPPING & DELIVERY
══════════════════════════════════════════════════════════════ */
const ShippingContent = () => (
  <div className="prose prose-lg max-w-none text-gray-600">
    <p className="text-base leading-relaxed mb-2">
      Book My Fresh Private Limited provides delivery services for fruits, vegetables, and grocery products through
      its website and mobile application.
    </p>
    <p className="text-sm text-gray-400 mb-6">Effective Date: June 2026</p>

    <SectionHeading>1. Service Areas</SectionHeading>
    <ul className="space-y-3 list-none pl-0">
      <Bullet>Currently, Book My Fresh provides services in selected areas of Jaipur, Rajasthan.</Bullet>
      <Bullet>The Company may expand its services to additional cities and regions in the future.</Bullet>
      <Bullet>Delivery availability depends on the customer's PIN code and serviceability.</Bullet>
    </ul>

    <SectionHeading>2. Delivery Types</SectionHeading>
    <SubHeading>Instant / On-Demand Delivery</SubHeading>
    <ul className="space-y-3 list-none pl-0">
      <Bullet>Available in selected PIN codes between <strong>6:00 AM and 11:00 PM</strong>.</Bullet>
      <Bullet>Target delivery time: approximately <strong>30–40 minutes</strong> from order confirmation.</Bullet>
      <Bullet>Free delivery on orders of <strong>₹199 and above</strong>. Delivery charges apply on orders below ₹199.</Bullet>
      <Bullet>Actual delivery times may vary due to order volume, weather conditions, traffic, and other operational considerations.</Bullet>
    </ul>
    <SubHeading>Scheduled / Next-Day Delivery</SubHeading>
    <ul className="space-y-3 list-none pl-0">
      <Bullet>Orders are delivered between <strong>5:00 AM and 9:00 AM</strong> on the selected delivery date.</Bullet>
      <Bullet>Free delivery on orders of <strong>₹199 and above</strong>. Delivery charges apply on orders below ₹199.</Bullet>
    </ul>
    <p className="mt-3 text-sm">
      Applicable delivery charges will be clearly displayed at checkout before order confirmation. Delivery charges
      may vary based on order value, delivery location, selected delivery option, and promotional offers.
    </p>

    <SectionHeading>3. Order Processing</SectionHeading>
    <ul className="space-y-3 list-none pl-0">
      <Bullet>Orders are processed after successful order confirmation and payment verification, where applicable.</Bullet>
      <Bullet>Book My Fresh reserves the right to refuse or cancel orders that cannot be fulfilled due to stock, logistics, or operational limitations.</Bullet>
    </ul>

    <SectionHeading>4. Delivery Attempts</SectionHeading>
    <p className="mb-3">Customers are responsible for providing accurate delivery details. If delivery cannot be completed because:</p>
    <ul className="space-y-3 list-none pl-0">
      <Bullet>Customer is unavailable</Bullet>
      <Bullet>Contact number is unreachable</Bullet>
      <Bullet>Address is incorrect or incomplete</Bullet>
      <Bullet>Refusal to accept the delivery</Bullet>
    </ul>
    <p className="mt-3">
      The order will be cancelled. Upon cancellation, the eligible refund amount will be processed either to the
      original payment method or as Book My Fresh Store Credits, in accordance with the customer's preference and
      our applicable Refund Policy.
    </p>

    <SectionHeading>5. Product Availability</SectionHeading>
    <p className="mb-3">All products are subject to availability. In case a product becomes unavailable after order placement, Book My Fresh may:</p>
    <ul className="space-y-3 list-none pl-0">
      <Bullet>Substitute the item with customer approval (sought via phone call, WhatsApp, SMS, or in-app notification before dispatch — no substitution will be made without customer consent)</Bullet>
      <Bullet>Remove the item from the order</Bullet>
      <Bullet>Issue a refund for the unavailable item</Bullet>
    </ul>

    <SectionHeading>6. Ownership and Risk</SectionHeading>
    <ul className="space-y-3 list-none pl-0">
      <Bullet>Ownership and responsibility for products transfer to the customer upon successful delivery.</Bullet>
      <Bullet>Customers are advised to inspect products immediately upon receipt. Any quality issues should be reported within <strong>24 hours of delivery</strong> in accordance with our Refund & Cancellation Policy.</Bullet>
    </ul>

    <SectionHeading>7. Modifications</SectionHeading>
    <p>
      We reserve the right to modify delivery charges, free-delivery thresholds, delivery timings, and serviceable
      areas at any time. Any such changes will be updated on the App and will apply prospectively.
    </p>

    <ContactBox>
      <p className="font-semibold text-gray-800 mb-2">Contact for Delivery Issues</p>
      <p className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-[#548B2E]" /> <a href="mailto:grievances@bookmyfresh.com" className="text-[#548B2E] font-medium">grievances@bookmyfresh.com</a></p>
      <p className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-[#548B2E]" /> +91 8078622096</p>
      <p className="text-gray-500">General enquiries: <a href="mailto:info@bookmyfresh.com" className="text-[#548B2E]">info@bookmyfresh.com</a></p>
    </ContactBox>
  </div>
);

/* ══════════════════════════════════════════════════════════════
   REFUND & CANCELLATION
══════════════════════════════════════════════════════════════ */
const RefundContent = () => (
  <div className="prose prose-lg max-w-none text-gray-600">
    <p className="text-base leading-relaxed mb-2">
      Book My Fresh Private Limited is committed to providing fresh fruits, vegetables, and grocery products to
      customers. This Refund & Cancellation Policy explains the terms governing cancellations, refunds, and
      replacements.
    </p>
    <p className="text-sm text-gray-400 mb-6">Effective Date: June 2026</p>

    <SectionHeading>1. Order Cancellation</SectionHeading>
    <p className="mb-3">Customers may request cancellation of an order at any time, including after dispatch, subject to operational feasibility.</p>
    <p className="mb-3">Book My Fresh reserves the right to cancel any order due to:</p>
    <ul className="space-y-3 list-none pl-0">
      <Bullet>Product unavailability</Bullet>
      <Bullet>Technical issues or payment failure</Bullet>
      <Bullet>Pricing errors</Bullet>
      <Bullet>Operational constraints</Bullet>
      <Bullet>Suspected fraudulent activity</Bullet>
    </ul>
    <p className="mt-3">
      Where Book My Fresh cancels a confirmed order, the full payment received will be refunded to the original
      payment source within <strong>4–5 business days</strong> of cancellation. The customer will be notified of
      the cancellation and reason, where operationally feasible.
    </p>

    <SectionHeading>2. Quality Issues and Complaints</SectionHeading>
    <ul className="space-y-3 list-none pl-0">
      <Bullet>Customers must report any product quality issues, damaged products, missing items, or incorrect deliveries within <strong>24 hours of receiving the order</strong>.</Bullet>
      <Bullet>Complaints received after 24 hours may not be eligible for refund or replacement. This is an operational requirement to enable prompt verification and resolution.</Bullet>
      <Bullet>Nothing in this policy limits or excludes any right available to customers under the Consumer Protection Act, 2019 or any other applicable law.</Bullet>
    </ul>

    <SectionHeading>3. Refund Eligibility</SectionHeading>
    <p className="mb-3">Refunds may be provided in the following situations:</p>
    <ul className="space-y-3 list-none pl-0">
      <Bullet>Damaged or poor-quality products</Bullet>
      <Bullet>Incorrect products delivered</Bullet>
      <Bullet>Missing items</Bullet>
      <Bullet>Orders cancelled by Book My Fresh</Bullet>
      <Bullet>Other genuine cases approved by our support team</Bullet>
    </ul>
    <p className="mt-3">
      Refund eligibility will be assessed upon verification of the complaint. Where a complaint is verified, a refund
      or replacement will be provided and the outcome communicated within <strong>48 hours</strong> of receiving
      the complaint and supporting evidence.
    </p>

    <SectionHeading>4. Replacement Policy</SectionHeading>
    <p className="mb-3">Where applicable, Book My Fresh may offer product replacement instead of a refund. Replacement availability depends on:</p>
    <ul className="space-y-3 list-none pl-0">
      <Bullet>Product stock availability</Bullet>
      <Bullet>Service area coverage</Bullet>
      <Bullet>Nature of the complaint</Bullet>
    </ul>

    <SectionHeading>5. Refund Processing</SectionHeading>
    <ul className="space-y-3 list-none pl-0">
      <Bullet>Approved refunds will be processed to the original payment source within <strong>4–5 business days</strong> of approval, subject to the processing timelines of the respective bank or payment service provider.</Bullet>
      <Bullet>At the customer's request, refunds may alternatively be credited to <strong>Book My Fresh Store Credits (BMF Coins)</strong> for faster processing.</Bullet>
      <Bullet>Refunds may also be processed to: original payment source, bank account, or UPI account.</Bullet>
      <Bullet>Once a refund is approved and initiated by Book My Fresh, the amount will generally be reflected within 4–5 business days, subject to banking partner timelines.</Bullet>
    </ul>

    <SectionHeading>6. Non-Refundable Situations</SectionHeading>
    <p className="mb-3">Refunds may not be provided in the following cases:</p>
    <ul className="space-y-3 list-none pl-0">
      <Bullet>Complaint raised after 24 hours of delivery</Bullet>
      <Bullet>Incorrect delivery address provided by customer</Bullet>
      <Bullet>Customer unavailable at delivery location</Bullet>
      <Bullet>Minor variations in size, colour, shape, or appearance of natural agricultural products that do not affect the quality, safety, or edibility of the product. This exclusion does not apply where the product is unfit for consumption, mouldy, rotten, or otherwise fails to meet basic quality standards.</Bullet>
      <Bullet>Issues caused by improper storage after delivery</Bullet>
    </ul>

    <ContactBox>
      <p className="font-semibold text-gray-800 mb-2">Contact for Refund & Cancellation Requests</p>
      <p><strong>Grievance Officer:</strong> Sheetal Ojha</p>
      <p className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-[#548B2E]" /> <a href="mailto:grievances@bookmyfresh.com" className="text-[#548B2E] font-medium">grievances@bookmyfresh.com</a></p>
      <p className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-[#548B2E]" /> +91 8078622096</p>
      <p className="text-gray-500">General enquiries: <a href="mailto:info@bookmyfresh.com" className="text-[#548B2E]">info@bookmyfresh.com</a></p>
    </ContactBox>
  </div>
);

/* ══════════════════════════════════════════════════════════════
   TAB HEADER CONFIG
══════════════════════════════════════════════════════════════ */
const TAB_META = {
  privacy:  { icon: <Lock className="w-10 h-10 text-[#548B2E] mr-4" />,   title: 'Privacy Policy',        updated: 'June 2026' },
  terms:    { icon: <FileText className="w-10 h-10 text-[#548B2E] mr-4" />, title: 'Terms & Conditions',   updated: 'June 2026' },
  shipping: { icon: <Truck className="w-10 h-10 text-[#548B2E] mr-4" />,   title: 'Shipping & Delivery Policy', updated: 'June 2026' },
  refund:   { icon: <RotateCcw className="w-10 h-10 text-[#548B2E] mr-4" />, title: 'Refund & Cancellation Policy', updated: 'June 2026' },
};

const CONTENT = {
  privacy:  <PrivacyContent />,
  terms:    <TermsContent />,
  shipping: <ShippingContent />,
  refund:   <RefundContent />,
};

/* ══════════════════════════════════════════════════════════════
   PAGE
══════════════════════════════════════════════════════════════ */
const Legal = () => {
  const location = useLocation();
  const [activeSection, setActiveSection] = useState('privacy');

  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash && TABS.some((t) => t.id === hash)) setActiveSection(hash);
  }, [location.hash]);

  const meta = TAB_META[activeSection];

  return (
    <div className="legal-page">
      {/* Hero */}
      <section className="relative min-h-[50vh] bg-black flex items-center justify-center">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-50"
          style={{ backgroundImage: `url('https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1600')` }}
        />
        <div className="relative z-10 text-center text-white px-4">
          <p className="text-[#FF9800] uppercase tracking-[0.3em] text-sm mb-4 font-medium">Transparency & Trust</p>
          <h1 className="text-5xl md:text-7xl font-bold mb-6" style={{ fontFamily: 'serif' }}>Legal</h1>
          <div className="w-24 h-1 bg-[#FF9800] mx-auto" />
        </div>
      </section>

      {/* Intro */}
      <section className="py-14 bg-white">
        <div className="container mx-auto px-4 max-w-3xl text-center">
          <p className="text-[#548B2E] uppercase tracking-[0.2em] text-sm mb-4 font-medium">Our Policies</p>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6" style={{ fontFamily: 'serif' }}>
            Book My Fresh Legal Documents
          </h2>
          <p className="text-gray-600 text-lg leading-relaxed">
            Trust is the foundation of Book My Fresh. Just as we promise transparency in our products, we extend
            the same to how we handle your data, orders, deliveries, and refunds. Please read these policies
            carefully before using our Platform.
          </p>
        </div>
      </section>

      {/* Tabs + Content */}
      <section className="py-12 bg-[#f8f9fa]">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Tab bar */}
          <div className="flex flex-wrap justify-center gap-3 mb-10">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveSection(tab.id)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm transition-all duration-200 ${
                  activeSection === tab.id
                    ? 'bg-[#548B2E] text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-gray-100 shadow-md'
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>

          {/* Content card */}
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
            <div className="flex items-start mb-8 pb-6 border-b border-gray-100">
              {meta.icon}
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-800" style={{ fontFamily: 'serif' }}>
                  {meta.title}
                </h2>
                <p className="text-xs text-gray-400 mt-1">Book My Fresh Private Limited &nbsp;·&nbsp; Effective: {meta.updated}</p>
              </div>
            </div>
            {CONTENT[activeSection]}
          </div>
        </div>
      </section>

      {/* Footer band */}
      <section className="py-16 bg-[#2d3436] text-white">
        <div className="container mx-auto px-4 max-w-3xl text-center">
          <Shield className="w-14 h-14 text-[#FF9800] mx-auto mb-5" />
          <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'serif' }}>
            Our Commitment to You
          </h2>
          <p className="text-gray-300 text-lg leading-relaxed">
            We built Book My Fresh on trust and transparency. Our legal pages are not fine print — they are part
            of the same philosophy that makes our promise to deliver fresh, quality produce possible.
          </p>
        </div>
      </section>

      {/* Contact band */}
      <section className="py-12 bg-[#f2f8eb]">
        <div className="container mx-auto px-4 max-w-xl text-center">
          <AlertCircle className="w-10 h-10 text-[#548B2E] mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-800 mb-3">Legal Enquiries</h3>
          <p className="text-gray-600 mb-2">
            For any legal questions or policy-related concerns, contact our Grievance Officer:
          </p>
          <p className="font-semibold text-gray-800">Sheetal Ojha</p>
          <a href="mailto:grievances@bookmyfresh.com" className="text-[#548B2E] font-semibold hover:underline">
            grievances@bookmyfresh.com
          </a>
          <p className="text-gray-500 text-sm mt-3">Book My Fresh Private Limited &nbsp;·&nbsp; Jaipur, Rajasthan</p>
        </div>
      </section>
    </div>
  );
};

export default Legal;
